import { useState, useEffect, useCallback } from 'react';
import { BASE, API_V1, authFetch } from '../constants';

/* ── Platform tools are fixed — defined by the MCP server ── */
const PLATFORM_TOOLS = [
  { name: 'search_exceptions', description: 'Search exceptions with filters' },
  { name: 'query_my_inbox', description: 'Items pending your approval' },
  { name: 'get_exception_detail', description: 'Full exception dossier' },
  { name: 'search_knowledge', description: 'Search knowledge base' },
  { name: 'get_financial_summary', description: 'Financial metrics & JEs' },
  { name: 'get_workflow_status', description: 'Workflow execution status' },
  { name: 'get_platform_health', description: 'System health metrics' },
  { name: 'get_my_history', description: 'Your action history' },
];

/* ── Helpers ── */
function timeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const CARD: React.CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 8,
  padding: 16,
};

const STAT_NUM: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  fontFamily: 'var(--font-mono)',
};

const BADGE_BASE: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
};

const SOURCE_BADGE: Record<string, React.CSSProperties> = {
  MCP: { ...BADGE_BASE, background: 'rgba(0,180,216,0.15)', color: '#00b4d8' },
  WEBHOOK: { ...BADGE_BASE, background: 'rgba(255,183,77,0.15)', color: '#ffb74d' },
  PLUGIN: { ...BADGE_BASE, background: 'rgba(186,104,200,0.15)', color: '#ba68c8' },
};

const STATUS_COLORS: Record<string, string> = {
  SUCCESS: '#4caf50',
  FAILED: '#ef5350',
  DENIED: '#ffa726',
};

const TH: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  borderBottom: '1px solid var(--border-subtle)',
};

const TD: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 12,
  color: 'var(--text-primary)',
  borderBottom: '1px solid var(--border-subtle)',
};

const EMPTY: React.CSSProperties = {
  textAlign: 'center',
  padding: 40,
  color: 'var(--text-muted)',
  fontSize: 13,
};

const BTN: React.CSSProperties = {
  padding: '6px 14px',
  fontSize: 11,
  fontWeight: 600,
  borderRadius: 6,
  cursor: 'pointer',
  border: '1px solid var(--border-subtle)',
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
};

const BTN_ACCENT: React.CSSProperties = {
  ...BTN,
  background: 'var(--accent)',
  color: 'var(--text-primary)',
  border: 'none',
};

/* ══════════════════════════════════════════════════════════════
   MCP Server Tab
   ══════════════════════════════════════════════════════════════ */
function MCPServerTab() {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    authFetch(`${API_V1}/mcp/stats`)
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(d => { setStats(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={EMPTY}>Loading MCP stats...</div>;
  if (error) return <div style={EMPTY}>Error loading MCP stats: {error}</div>;
  if (!stats) return <div style={EMPTY}>No data available</div>;

  const copyEndpoint = () => {
    navigator.clipboard.writeText(stats.endpoint || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const statCards = [
    { label: 'Total Tools', value: stats.total_tools ?? 0 },
    { label: 'Platform Tools', value: stats.platform_tools ?? PLATFORM_TOOLS.length },
    { label: 'Skill Tools', value: stats.skill_tools?.total ?? 0 },
    { label: 'Server Status', value: stats.server_status ?? 'unknown', isStatus: true },
  ];

  return (
    <div>
      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {statCards.map(c => (
          <div key={c.label} style={CARD}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{c.label}</div>
            {c.isStatus ? (
              <div style={{ ...STAT_NUM, fontSize: 16, color: c.value === 'running' ? '#4caf50' : '#ef5350' }}>
                <span style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                  background: c.value === 'running' ? '#4caf50' : '#ef5350',
                  marginRight: 8, verticalAlign: 'middle',
                }} />
                {String(c.value).toUpperCase()}
              </div>
            ) : (
              <div style={STAT_NUM}>{c.value}</div>
            )}
          </div>
        ))}
      </div>

      {/* Endpoint URL */}
      <div style={{ ...CARD, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>MCP Endpoint</div>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-primary)',
            background: 'rgba(0,0,0,0.2)', padding: '6px 12px', borderRadius: 4,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {stats.endpoint || 'Not configured'}
          </div>
        </div>
        <button onClick={copyEndpoint} style={BTN}>{copied ? 'Copied!' : 'Copy'}</button>
      </div>

      {/* Platform tools table */}
      <div style={{ ...CARD, marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Platform Tools</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>Tool Name</th>
              <th style={TH}>Description</th>
            </tr>
          </thead>
          <tbody>
            {PLATFORM_TOOLS.map(t => (
              <tr key={t.name}>
                <td style={{ ...TD, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{t.name}</td>
                <td style={{ ...TD, color: 'var(--text-secondary)' }}>{t.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Skill tools by engine */}
      {stats.skill_tools?.by_engine && (
        <div style={CARD}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Skill Tools by Engine</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Engine</th>
                <th style={TH}>Count</th>
                <th style={TH}>Tier</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stats.skill_tools.by_engine).map(([engine, count]: [string, any]) => {
                const isTier2 = stats.skill_tools.tier2_excluded?.includes?.(engine);
                return (
                  <tr key={engine}>
                    <td style={{ ...TD, fontFamily: 'var(--font-mono)' }}>{engine}</td>
                    <td style={TD}>{count}</td>
                    <td style={TD}>
                      {isTier2 ? (
                        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                          <span role="img" aria-label="locked" style={{ marginRight: 4 }}>&#128274;</span>Tier 2 (excluded)
                        </span>
                      ) : (
                        <span style={{ color: '#4caf50', fontSize: 11 }}>Active</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Plugins Tab
   ══════════════════════════════════════════════════════════════ */
function PluginsTab({ isAdmin }: { isAdmin: boolean }) {
  const [available, setAvailable] = useState<any[]>([]);
  const [installed, setInstalled] = useState<any[]>([]);
  const [hookStats, setHookStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([
      authFetch(`${API_V1}/plugins/available`).then(r => r.ok ? r.json() : []),
      authFetch(`${API_V1}/plugins/installed`).then(r => r.ok ? r.json() : []),
      authFetch(`${API_V1}/plugins/hooks/stats`).then(r => r.ok ? r.json() : null),
    ])
      .then(([a, i, h]) => { setAvailable(a); setInstalled(i); setHookStats(h); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const installPlugin = async (name: string) => {
    setBusy(name);
    try {
      const r = await authFetch(`${API_V1}/plugins/install`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plugin_name: name, config: {} }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `Install failed (${r.status})`);
      }
      load();
    } catch (e: any) {
      alert(`Failed to install ${name}: ${e.message}`);
    } finally {
      setBusy('');
    }
  };

  const uninstallPlugin = async (name: string) => {
    if (!window.confirm(`Uninstall plugin "${name}"? This cannot be undone.`)) return;
    setBusy(name);
    try {
      const r = await authFetch(`${API_V1}/plugins/uninstall/${encodeURIComponent(name)}`, { method: 'POST' });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `Uninstall failed (${r.status})`);
      }
      load();
    } catch (e: any) {
      alert(`Failed to uninstall ${name}: ${e.message}`);
    } finally {
      setBusy('');
    }
  };

  if (loading) return <div style={EMPTY}>Loading plugins...</div>;
  if (error) return <div style={EMPTY}>Error loading plugins: {error}</div>;

  const installedNames = new Set(installed.map((p: any) => p.name || p.plugin_name));

  return (
    <div>
      {/* Installed plugins */}
      <div style={{ ...CARD, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Installed Plugins</div>
          <button onClick={load} style={BTN}>Refresh</button>
        </div>
        {installed.length === 0 ? (
          <div style={EMPTY}>No plugins installed</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Plugin</th>
                <th style={TH}>Version</th>
                <th style={TH}>Status</th>
                <th style={TH}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {installed.map((p: any) => {
                const pName = p.name || p.plugin_name;
                return (
                  <tr key={pName}>
                    <td style={{ ...TD, fontWeight: 600 }}>{pName}</td>
                    <td style={{ ...TD, fontFamily: 'var(--font-mono)' }}>{p.version || '-'}</td>
                    <td style={TD}>
                      <span style={{ color: '#4caf50', fontSize: 11, fontWeight: 600 }}>ACTIVE</span>
                    </td>
                    <td style={TD}>
                      {isAdmin && (
                        <button
                          onClick={() => uninstallPlugin(pName)}
                          disabled={busy === pName}
                          style={{ ...BTN, color: '#ef5350', fontSize: 10 }}
                        >
                          {busy === pName ? 'Removing...' : 'Uninstall'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Available plugins */}
      <div style={{ ...CARD, marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Available Plugins</div>
        {available.length === 0 ? (
          <div style={EMPTY}>No plugins available</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Plugin</th>
                <th style={TH}>Description</th>
                <th style={TH}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {available.map((p: any) => {
                const pName = p.name || p.plugin_name;
                const alreadyInstalled = installedNames.has(pName);
                return (
                  <tr key={pName}>
                    <td style={{ ...TD, fontWeight: 600 }}>{pName}</td>
                    <td style={{ ...TD, color: 'var(--text-secondary)' }}>{p.description || '-'}</td>
                    <td style={TD}>
                      {alreadyInstalled ? (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Installed</span>
                      ) : isAdmin ? (
                        <button
                          onClick={() => installPlugin(pName)}
                          disabled={busy === pName}
                          style={{ ...BTN_ACCENT, fontSize: 10 }}
                        >
                          {busy === pName ? 'Installing...' : 'Install'}
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Admin only</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Hook stats */}
      {hookStats && (
        <div style={CARD}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Hook Statistics</div>
          {typeof hookStats === 'object' && !Array.isArray(hookStats) ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>Hook</th>
                  <th style={TH}>Invocations</th>
                  <th style={TH}>Avg Duration</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(hookStats).map(([hook, data]: [string, any]) => (
                  <tr key={hook}>
                    <td style={{ ...TD, fontFamily: 'var(--font-mono)' }}>{hook}</td>
                    <td style={TD}>{typeof data === 'object' ? data.count ?? data.invocations ?? '-' : data}</td>
                    <td style={{ ...TD, fontFamily: 'var(--font-mono)' }}>
                      {typeof data === 'object' && data.avg_duration_ms != null ? `${data.avg_duration_ms}ms` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={EMPTY}>No hook data available</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Webhooks Tab
   ══════════════════════════════════════════════════════════════ */
function WebhooksTab({ isAdmin }: { isAdmin: boolean }) {
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copiedId, setCopiedId] = useState('');

  // Create form state
  const [formName, setFormName] = useState('');
  const [formAction, setFormAction] = useState<'WORKFLOW' | 'EVENT'>('WORKFLOW');
  const [formWorkflow, setFormWorkflow] = useState('');
  const [formEvent, setFormEvent] = useState('');
  const [formEntity, setFormEntity] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    authFetch(`${API_V1}/webhooks/`)
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(d => { setWebhooks(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyUrl = (webhook: any) => {
    const url = BASE + webhook.url;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(webhook.webhook_id);
      setTimeout(() => setCopiedId(''), 2000);
    });
  };

  const deleteWebhook = async (id: string, name: string) => {
    if (!window.confirm(`Delete webhook "${name}"? This cannot be undone.`)) return;
    try {
      const r = await authFetch(`${API_V1}/webhooks/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`Delete failed (${r.status})`);
      load();
    } catch (e: any) {
      alert(`Failed to delete: ${e.message}`);
    }
  };

  const createWebhook = async () => {
    if (!formName.trim()) { alert('Name is required'); return; }
    setBusy(true);
    try {
      const body: any = { name: formName.trim(), action_type: formAction };
      if (formAction === 'WORKFLOW' && formWorkflow.trim()) body.workflow_code = formWorkflow.trim();
      if (formAction === 'EVENT' && formEvent.trim()) body.event_name = formEvent.trim();
      if (formEntity.trim()) body.entity_code = formEntity.trim();

      const r = await authFetch(`${API_V1}/webhooks/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Create failed (${r.status})`);
      }
      const result = await r.json();

      // Show secret ONCE
      alert(
        `Webhook created successfully!\n\n` +
        `URL: ${BASE}${result.url}\n\n` +
        `SECRET: ${result.secret}\n\n` +
        `IMPORTANT: Copy this secret now. It will not be shown again.`
      );

      setShowCreate(false);
      setFormName('');
      setFormWorkflow('');
      setFormEvent('');
      setFormEntity('');
      load();
    } catch (e: any) {
      alert(`Failed to create webhook: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div style={EMPTY}>Loading webhooks...</div>;
  if (error) return <div style={EMPTY}>Error loading webhooks: {error}</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Webhooks ({webhooks.length})</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={BTN}>Refresh</button>
          {isAdmin && (
            <button onClick={() => setShowCreate(!showCreate)} style={BTN_ACCENT}>
              {showCreate ? 'Cancel' : '+ Create Webhook'}
            </button>
          )}
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div style={{ ...CARD, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>New Webhook</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Name *</label>
              <input
                value={formName} onChange={e => setFormName(e.target.value)}
                placeholder="e.g. Slack Notification"
                style={{
                  width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 4,
                  border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)',
                  color: 'var(--text-primary)', boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Action Type</label>
              <select
                value={formAction} onChange={e => setFormAction(e.target.value as 'WORKFLOW' | 'EVENT')}
                style={{
                  width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 4,
                  border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)',
                  color: 'var(--text-primary)', boxSizing: 'border-box',
                }}
              >
                <option value="WORKFLOW">WORKFLOW</option>
                <option value="EVENT">EVENT</option>
              </select>
            </div>
            {formAction === 'WORKFLOW' && (
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Workflow Code</label>
                <input
                  value={formWorkflow} onChange={e => setFormWorkflow(e.target.value)}
                  placeholder="e.g. GL_RECONCILIATION"
                  style={{
                    width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 4,
                    border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)',
                    color: 'var(--text-primary)', boxSizing: 'border-box',
                  }}
                />
              </div>
            )}
            {formAction === 'EVENT' && (
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Event Name</label>
                <input
                  value={formEvent} onChange={e => setFormEvent(e.target.value)}
                  placeholder="e.g. data_uploaded"
                  style={{
                    width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 4,
                    border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)',
                    color: 'var(--text-primary)', boxSizing: 'border-box',
                  }}
                />
              </div>
            )}
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Entity Code (optional)</label>
              <input
                value={formEntity} onChange={e => setFormEntity(e.target.value)}
                placeholder="e.g. USA"
                style={{
                  width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 4,
                  border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)',
                  color: 'var(--text-primary)', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
          <button onClick={createWebhook} disabled={busy} style={BTN_ACCENT}>
            {busy ? 'Creating...' : 'Create Webhook'}
          </button>
        </div>
      )}

      {/* Webhook list */}
      {webhooks.length === 0 ? (
        <div style={{ ...CARD, ...EMPTY }}>No webhooks configured</div>
      ) : (
        <div style={CARD}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Name</th>
                <th style={TH}>Type</th>
                <th style={TH}>Target</th>
                <th style={TH}>Status</th>
                <th style={TH}>Triggers</th>
                <th style={TH}>Last Triggered</th>
                <th style={TH}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {webhooks.map((wh: any) => (
                <tr key={wh.webhook_id}>
                  <td style={{ ...TD, fontWeight: 600 }}>{wh.name}</td>
                  <td style={TD}>
                    <span style={SOURCE_BADGE.WEBHOOK}>{wh.action_type}</span>
                  </td>
                  <td style={{ ...TD, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                    {wh.workflow_code || wh.event_name || '-'}
                  </td>
                  <td style={TD}>
                    <span style={{
                      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                      background: wh.active ? '#4caf50' : '#ef5350', marginRight: 6, verticalAlign: 'middle',
                    }} />
                    <span style={{ fontSize: 11 }}>{wh.active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td style={{ ...TD, fontFamily: 'var(--font-mono)' }}>{wh.trigger_count ?? 0}</td>
                  <td style={{ ...TD, fontSize: 11, color: 'var(--text-secondary)' }}>
                    {wh.last_triggered_at ? (
                      <span title={new Date(wh.last_triggered_at).toLocaleString()}>
                        {timeAgo(wh.last_triggered_at)}
                      </span>
                    ) : 'Never'}
                  </td>
                  <td style={TD}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => copyUrl(wh)} style={{ ...BTN, fontSize: 10 }}>
                        {copiedId === wh.webhook_id ? 'Copied!' : 'Copy URL'}
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => deleteWebhook(wh.webhook_id, wh.name)}
                          style={{ ...BTN, fontSize: 10, color: '#ef5350' }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Audit Trail Tab
   ══════════════════════════════════════════════════════════════ */
function AuditTrailTab() {
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams({ limit: '50' });
    if (sourceFilter) params.set('source', sourceFilter);
    if (statusFilter) params.set('status', statusFilter);
    authFetch(`${API_V1}/mcp/audit?${params.toString()}`)
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(d => { setItems(d.items || []); setTotal(d.total || 0); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [sourceFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const durationColor = (ms: number | null | undefined) => {
    if (ms == null) return 'var(--text-muted)';
    if (ms < 100) return '#4caf50';
    if (ms < 500) return '#ffa726';
    return '#ef5350';
  };

  const SELECT_STYLE: React.CSSProperties = {
    padding: '4px 8px', fontSize: 11, borderRadius: 4,
    border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)',
    color: 'var(--text-primary)',
  };

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <div>
          <label style={{ fontSize: 10, color: 'var(--text-muted)', marginRight: 6 }}>Source</label>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} style={SELECT_STYLE}>
            <option value="">All</option>
            <option value="MCP">MCP</option>
            <option value="WEBHOOK">WEBHOOK</option>
            <option value="PLUGIN">PLUGIN</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 10, color: 'var(--text-muted)', marginRight: 6 }}>Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={SELECT_STYLE}>
            <option value="">All</option>
            <option value="SUCCESS">SUCCESS</option>
            <option value="FAILED">FAILED</option>
            <option value="DENIED">DENIED</option>
          </select>
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{total} total entries</span>
        <button onClick={load} style={BTN}>Refresh</button>
      </div>

      {loading ? (
        <div style={EMPTY}>Loading audit trail...</div>
      ) : error ? (
        <div style={EMPTY}>Error loading audit trail: {error}</div>
      ) : items.length === 0 ? (
        <div style={{ ...CARD, ...EMPTY }}>No audit entries found</div>
      ) : (
        <div style={CARD}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...TH, width: 30 }} />
                <th style={TH}>Time</th>
                <th style={TH}>Tool</th>
                <th style={TH}>Source</th>
                <th style={TH}>Status</th>
                <th style={TH}>Duration</th>
                <th style={TH}>Client</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item: any, idx: number) => (
                <>
                  <tr
                    key={idx}
                    onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ ...TD, fontSize: 10, color: 'var(--text-muted)' }}>
                      {expandedIdx === idx ? '\u25BC' : '\u25B6'}
                    </td>
                    <td style={{ ...TD, fontSize: 11, color: 'var(--text-secondary)' }}>
                      <span title={item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}>
                        {item.timestamp ? timeAgo(item.timestamp) : '-'}
                      </span>
                    </td>
                    <td style={{ ...TD, fontFamily: 'var(--font-mono)', fontSize: 12 }}>{item.tool || '-'}</td>
                    <td style={TD}>
                      <span style={SOURCE_BADGE[item.source] || { ...BADGE_BASE, background: 'rgba(128,128,128,0.15)', color: 'var(--text-muted)' }}>
                        {item.source || 'UNKNOWN'}
                      </span>
                    </td>
                    <td style={TD}>
                      <span style={{
                        display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                        background: STATUS_COLORS[item.status] || '#999',
                        marginRight: 6, verticalAlign: 'middle',
                      }} />
                      <span style={{ fontSize: 11 }}>{item.status || '-'}</span>
                    </td>
                    <td style={{
                      ...TD,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                      color: durationColor(item.duration_ms),
                    }}>
                      {item.duration_ms != null ? `${item.duration_ms}ms` : '-'}
                    </td>
                    <td style={{ ...TD, fontSize: 11, color: 'var(--text-secondary)' }}>{item.client || '-'}</td>
                  </tr>
                  {expandedIdx === idx && (
                    <tr key={`${idx}-detail`}>
                      <td colSpan={7} style={{ padding: '8px 12px 16px 40px', borderBottom: '1px solid var(--border-subtle)' }}>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>Parameters</div>
                        <pre style={{
                          fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-secondary)',
                          background: 'rgba(0,0,0,0.2)', padding: 12, borderRadius: 4,
                          margin: 0, overflow: 'auto', maxHeight: 300,
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          {item.parameters ? JSON.stringify(item.parameters, null, 2) : 'No parameters'}
                        </pre>
                        {item.ip && (
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 8 }}>
                            IP: <span style={{ fontFamily: 'var(--font-mono)' }}>{item.ip}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Main Export
   ══════════════════════════════════════════════════════════════ */
export default function IntegrationsTab() {
  const [subTab, setSubTab] = useState<'mcp' | 'plugins' | 'webhooks' | 'audit'>('mcp');
  const user = JSON.parse(localStorage.getItem('agentcfo_user') || '{}');
  const isAdmin = user?.role === 'ADMIN';

  const SUB_TABS = [
    { key: 'mcp' as const, label: 'MCP Server' },
    { key: 'plugins' as const, label: 'Plugins' },
    { key: 'webhooks' as const, label: 'Webhooks' },
    { key: 'audit' as const, label: 'Audit Trail' },
  ];

  return (
    <div>
      {/* Sub-tab navigation */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border-subtle)', marginBottom: 20 }}>
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={{
            padding: '8px 16px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
            background: 'transparent', border: 'none',
            color: subTab === t.key ? 'var(--accent)' : 'var(--text-muted)',
            borderBottom: subTab === t.key ? '2px solid var(--accent)' : '2px solid transparent',
          }}>{t.label}</button>
        ))}
      </div>
      {subTab === 'mcp' && <MCPServerTab />}
      {subTab === 'plugins' && <PluginsTab isAdmin={isAdmin} />}
      {subTab === 'webhooks' && <WebhooksTab isAdmin={isAdmin} />}
      {subTab === 'audit' && <AuditTrailTab />}
    </div>
  );
}
